import React, { useState, useMemo } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import {
  GROUPS_MARKETPLACE_PAGE,
  TOURNAMENT_QUICK_CREATE_PAGE,
  getCityAllClinicUrlWithSport,
  getCityAllLessonUrlWithSport,
  getEventUrl,
} from 'constants/pages';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { getEventLocation } from 'utils/shared/location/getEventLocation';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useModal } from 'hooks/useModal';
import ClinicDetailModal from 'screens/ExploreClinics/components/ClinicDetailModal';
import ClinicCard from 'screens/ExploreClinics/components/ClinicsList/ClinicCard';
import { ClinicType } from 'screens/ExploreClinics/props';
import CityPageText from 'screens/CityDetailPage/CityPageText';
import { ButtonLink } from 'components/Button';
import EmptyAction from 'components/EmptyAction';
import GroupCard from 'components/Explore/Groups/GroupCard';
import Link from 'components/Link';
import CardCoach from 'components/cards/CardCoach';
import CardTournament from 'components/cards/CardTournament';
import { Props } from './types';
import { getTitleLocationName } from './utils';

function formatDateRange(startDate: string, endDate: string): string {
  const yearOptions: Intl.DateTimeFormatOptions = { year: 'numeric' };
  const monthDayOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  const startMonthDay = new Intl.DateTimeFormat('en-US', monthDayOptions).format(
    new Date(startDate),
  );
  const endMonthDay = new Intl.DateTimeFormat('en-US', monthDayOptions).format(new Date(endDate));
  const year = new Intl.DateTimeFormat('en-US', yearOptions).format(new Date(endDate));

  return `${startMonthDay} - ${endMonthDay} ${year}`;
}

export default function CityExtraContent({
  city,
  sport,
  tournaments,
  stateTournaments,
  coaches,
  topCoaches,
  topClinics,
  coachStarterProfiles,
  groups,
}: {
  city: Props['city'];
  sport?: string;
  tournaments?: Props['tournaments'];
  stateTournaments?: Props['stateTournaments'];
  coaches?: Props['coaches'];
  topCoaches?: Props['topCoaches'];
  topClinics?: Props['topClinics'];
  coachStarterProfiles?: Props['coachStarterProfiles'];
  groups?: Props['groups'];
}) {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const isTournamentSport = sportName === 'Pickleball';
  const isGroupSport = sportName === 'Pickleball';

  const centerLatitude = city?.latitude;
  const centerLongitude = city?.longitude;

  const [selectedClinicForDetail, setSelectedClinicForDetail] = useState<ClinicType | null>(null);
  const clinicDetailModal = useModal();

  const handleClinicDetail = (clinic: ClinicType) => {
    setSelectedClinicForDetail(clinic);
    clinicDetailModal.openModal();
  };

  const decoratedTopCoaches = useMemo(() => {
    return (topCoaches || []).map((coach) => ({
      ...coach,
      distance: Math.round(
        calculateHaversineDistance({
          coord1: {
            latitude: coach?.geometry?.coordinates[1],
            longitude: coach?.geometry?.coordinates[0],
          },
          coord2: {
            latitude: centerLatitude,
            longitude: centerLongitude,
          },
          unit: 'miles',
        }),
      ),
    }));
  }, [topCoaches, centerLatitude, centerLongitude]);

  const decoratedTopClinics = useMemo(() => {
    return (topClinics || []).map((clinic) => ({
      ...clinic,
      distance: Math.round(
        calculateHaversineDistance({
          coord1: {
            latitude: clinic?.venue?.geometry?.coordinates[1] || clinic?.venue?.latitude || 0,
            longitude: clinic?.venue?.geometry?.coordinates[0] || clinic?.venue?.longitude || 0,
          },
          coord2: {
            latitude: centerLatitude,
            longitude: centerLongitude,
          },
          unit: 'miles',
        }),
      ),
    }));
  }, [topClinics, centerLatitude, centerLongitude]);

  const decoratedGroups = useMemo(() => {
    return (groups || []).map((group) => ({
      ...group,
      distance: Math.round(
        calculateHaversineDistance({
          coord1: {
            latitude: group.latitude,
            longitude: group.longitude,
          },
          coord2: {
            latitude: centerLatitude,
            longitude: centerLongitude,
          },
          unit: 'miles',
        }),
      ),
    }));
  }, [groups, centerLatitude, centerLongitude]);

  const decoratedTournaments = useMemo(() => {
    return (tournaments || []).map((tournament) => ({
      ...tournament,
      startTimestamp: new Date(tournament.startDateTime).getTime(),
      displayDate: formatDateRange(tournament.startDateTime, tournament.endDateTime),
      distance: Math.round(
        calculateHaversineDistance({
          coord1: {
            latitude: tournament.latitude,
            longitude: tournament.longitude,
          },
          coord2: {
            latitude: centerLatitude,
            longitude: centerLongitude,
          },
          unit: 'miles',
        }),
      ),
    }));
  }, [tournaments, centerLatitude, centerLongitude]);

  const decoratedStateTournaments = useMemo(() => {
    return (stateTournaments || [])
      .map((tournament) => ({
        ...tournament,
        startTimestamp: new Date(tournament.startDateTime).getTime(),
        displayDate: formatDateRange(tournament.startDateTime, tournament.endDateTime),
        distance: Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: tournament.latitude,
              longitude: tournament.longitude,
            },
            coord2: {
              latitude: centerLatitude,
              longitude: centerLongitude,
            },
            unit: 'miles',
          }),
        ),
      }))
      .toSorted((a, b) => a.distance - b.distance);
  }, [stateTournaments, centerLatitude, centerLongitude]);

  const cityNameFull = getTitleLocationName(city) || '';
  const cityNameShort = city?.name || '';

  return (
    <>
      <div className="flex grow flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary mt-16 pt-2 border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode">
        <div className="px-4">
          {/* Top Coaches Section */}
          {!!decoratedTopCoaches?.length && (
            <div className="mx-auto mt-ds-3xl max-w-7xl">
              <div className="relative mb-4 flex items-center justify-between leading-none">
                <Link href={getCityAllLessonUrlWithSport(city.slug, sport)}>
                  <h2 className="typography-product-heading">
                    Top {sportName} Lessons in {cityNameShort}
                  </h2>
                </Link>
              </div>
              <div>
                <div className="border-b border-color-border-input-lightmode pb-ds-3xl dark:border-color-border-input-darkmode">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
                    {decoratedTopCoaches.map((coach) => (
                      <CardCoach
                        key={coach.id}
                        id={coach.id}
                        slug={coach.username || coach.id}
                        photoUrl={getProfileImageUrlOrPlaceholder({
                          path: coach.profileImagePath || '',
                          placeholder: EMPTY_COACH_AVATAR_SRC,
                        })}
                        certificationImages={[]}
                        fullName={coach?.fullName || ''}
                        price={coach?.priceUnitAmountCoachDefault || 0}
                        distance={coach.distance}
                        currency={
                          coach?.stripeMerchantCurrency ||
                          coach?.currencyCoachDefault ||
                          CURRENCY_USD
                        }
                      />
                    ))}
                  </div>
                  <div className="mt-ds-2xl flex items-center">
                    <ButtonLink
                      href={getCityAllLessonUrlWithSport(city.slug, sport)}
                      variant="secondary"
                      size="lg"
                      isInline
                      className=""
                    >
                      View All {sportName} Lessons in {cityNameShort}
                    </ButtonLink>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Top Clinics Section */}
          {!!decoratedTopClinics?.length && (
            <div className="mx-auto mt-ds-3xl max-w-7xl">
              <div className="relative mb-4 flex items-center justify-between leading-none">
                <Link href={getCityAllClinicUrlWithSport(city.slug, sport)}>
                  <h2 className="typography-product-heading">
                    Top {sportName} Clinics in {cityNameShort}
                  </h2>
                </Link>
              </div>
              <div>
                <div className="border-b border-color-border-input-lightmode pb-ds-3xl dark:border-color-border-input-darkmode">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {decoratedTopClinics.map((clinic) => (
                      <ClinicCard
                        key={clinic.id}
                        clinic={clinic}
                        listHoveredClinicId={null}
                        setListHoveredClinicId={() => { }}
                        onClinicDetail={handleClinicDetail}
                      />
                    ))}
                  </div>
                  <div className="mt-ds-2xl flex items-center">
                    <ButtonLink
                      href={getCityAllClinicUrlWithSport(city.slug, sport)}
                      variant="secondary"
                      size="lg"
                      isInline
                    >
                      View All {sportName} Clinics in {cityNameShort}
                    </ButtonLink>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isGroupSport && !!decoratedGroups?.length && (
            <div className="mx-auto mt-ds-3xl max-w-7xl">
              <div className="relative mb-4 flex items-center justify-between leading-none">
                <h2 className="typography-product-heading">
                  {sportName} Groups in {cityNameShort}
                </h2>
              </div>
              <div>
                <div className="border-b border-color-border-input-lightmode pb-ds-3xl dark:border-color-border-input-darkmode">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {decoratedGroups.map((group) => (
                      <Link key={group.id} href={`${GROUPS_MARKETPLACE_PAGE}/${group.slug}`}>
                        <GroupCard group={{ ...group, events: [], members: [] }} />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Tournaments Section */}
          {isTournamentSport && decoratedTournaments && decoratedTournaments.length > 0 && (
            <div className="mx-auto mt-ds-3xl max-w-7xl">
              <div className="relative mb-4 flex items-center justify-between leading-none">
                <h2 className="typography-product-heading">
                  {sportName} Tournaments in {cityNameShort}
                </h2>
              </div>
              <div>
                <div className="border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
                  {decoratedTournaments.length > 0 ? (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2 xl:grid-cols-3">
                      {decoratedTournaments.slice(0, 6).map((tournament) => (
                        <Link
                          key={tournament.id}
                          className="block w-full"
                          href={getEventUrl(tournament.id)}
                        >
                          <CardTournament
                            key={tournament.id}
                            listHoveredTournamentId={null}
                            setListHoveredTournamentId={() => { }}
                            tournamentId={tournament.id}
                            coverImageUrl={
                              tournament.coverImagePath
                                ? getImageUrl({
                                  url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                                  path: tournament.coverImagePath,
                                })
                                : ''
                            }
                            organizerImageUrl={
                              tournament.organizerImagePath
                                ? getImageUrl({
                                  url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                                  path: tournament.organizerImagePath,
                                })
                                : ''
                            }
                            title={tournament.title}
                            registrationClosedAt={tournament.registrationClosedAt}
                            startTimestamp={tournament.startTimestamp}
                            status={tournament.status}
                            sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                            sourceRegistrationCount={tournament.sourceRegistrationCount}
                            registrationCount={
                              tournament.registrationsAggregate?.aggregate?.count || 0
                            }
                            isExternal={tournament.isExternal}
                            displayDate={tournament.displayDate}
                            displayLocation={getEventLocation(tournament)}
                            distance={tournament.distance}
                            showImageAndPeople={false}
                          />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <EmptyAction
                      description={`No upcoming tournaments in ${cityNameShort}`}
                      actionUrl={TOURNAMENT_QUICK_CREATE_PAGE}
                      actionText="Create Round Robin"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {isTournamentSport &&
            decoratedStateTournaments &&
            decoratedStateTournaments.length > 0 && (
              <div className="mx-auto mt-ds-3xl max-w-7xl">
                <div className="relative mb-4 flex items-center justify-between leading-none">
                  <h2 className="typography-product-heading">
                    All Upcoming {sportName} Tournaments in {city?.countrySubdivision?.name}
                  </h2>
                </div>
                <div>
                  <div className="border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
                    {decoratedStateTournaments.length > 0 ? (
                      <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2 xl:grid-cols-3">
                        {decoratedStateTournaments.map((tournament) => (
                          <Link
                            key={tournament.id}
                            className="block w-full"
                            href={getEventUrl(tournament.id)}
                          >
                            <CardTournament
                              key={tournament.id}
                              listHoveredTournamentId={null}
                              setListHoveredTournamentId={() => { }}
                              tournamentId={tournament.id}
                              coverImageUrl={
                                tournament.coverImagePath
                                  ? getImageUrl({
                                    url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                                    path: tournament.coverImagePath,
                                  })
                                  : ''
                              }
                              organizerImageUrl={
                                tournament.organizerImagePath
                                  ? getImageUrl({
                                    url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                                    path: tournament.organizerImagePath,
                                  })
                                  : ''
                              }
                              title={tournament.title}
                              registrationClosedAt={tournament.registrationClosedAt}
                              startTimestamp={tournament.startTimestamp}
                              status={tournament.status}
                              sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                              sourceRegistrationCount={tournament.sourceRegistrationCount}
                              registrationCount={
                                tournament.registrationsAggregate?.aggregate?.count || 0
                              }
                              isExternal={tournament.isExternal}
                              displayDate={tournament.displayDate}
                              displayLocation={
                                tournament.city?.name && tournament.city?.countrySubdivision?.code
                                  ? `${tournament.city?.name}, ${tournament.city?.countrySubdivision?.code}`
                                  : tournament.displayLocation
                              }
                              distance={tournament.distance}
                              showImageAndPeople={false}
                            />
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <EmptyAction
                        description={`No upcoming tournaments in ${city?.countrySubdivision?.name}`}
                        actionUrl={TOURNAMENT_QUICK_CREATE_PAGE}
                        actionText="Create Round Robin"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
        <CityPageText
          {...({
            tournaments: tournaments || [],
            stateTournaments: stateTournaments || [],
            venues: city?.venues || [],
            city,
            coaches: coaches || [],
            topCoaches: topCoaches || [],
            topClinics: topClinics || [],
            coachStarterProfiles: coachStarterProfiles || [],
            groups: groups || [],
            jsonLd: '',
            sport,
          } as any)}
          cityNameFull={cityNameFull}
          cityNameShort={cityNameShort}
          sport={sport}
        />
      </div>
      <ClinicDetailModal
        open={clinicDetailModal.isOpen}
        handleClose={clinicDetailModal.closeModal}
        clinic={selectedClinicForDetail as ClinicType}
      />
    </>
  );
}
